/// <amd-module name="typeahead/core/geolocation/GeolocationViewController" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>

import {GeolocationDataProvider} from "typeahead/core/geolocation/GeolocationDataProvider";
import {GeolocationRequestMessage} from "typeahead/core/geolocation/message/GeolocationRequestMessage";
import {GeolocationResponseMessage} from "typeahead/core/geolocation/message/GeolocationResponseMessage";
import {ElementFocusedMessage} from "typeahead/core/message/ElementFocusedMessage";
import {SuggestionSelectedMessage} from "typeahead/core/message/SuggestionSelectedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {ISuggestionResult} from "typeahead/core/Suggestion";

export interface IErrorConfig {
    code: number;
    closeTypeahead: boolean;
    message: string;
}

export abstract class GeolocationViewController {

    protected static readonly MESSAGES = {
        ERROR_TITLE: loc.geolocationErrorTitle || "We are unable to determine your current location",
        FINDING_GEOLOCATION: loc.geolocationFindingMessage || "Finding your current location",
        PERMISSION_DENIED: loc.geolocationPermissionDeniedMessage || "Please turn on the location services on your " +
        "device so we can determine your location or type in a destination.",
        POSITION_UNAVAILABLE: loc.geolocationPositionUnavailableMessage || "Please type in a destination.",
        TIMEOUT: loc.geolocationTimeoutMessage || "Please retry or type in a destination.",
        UNKNOWN_ERROR: loc.geolocationUnknownErrorMessage || "Please retry or type in a destination.",
    };

    protected domElementId: string;
    protected errorConfigMap: {[code: number]: IErrorConfig} = {
        1: {code: 1, closeTypeahead: true, message: GeolocationViewController.MESSAGES.PERMISSION_DENIED},
        2: {code: 2, closeTypeahead: true, message: GeolocationViewController.MESSAGES.POSITION_UNAVAILABLE},
        3: {code: 3, closeTypeahead: false, message: GeolocationViewController.MESSAGES.TIMEOUT},
        4: {code: 4, closeTypeahead: false, message: GeolocationViewController.MESSAGES.UNKNOWN_ERROR},
    };

    protected readonly interstitialContentSelector: string = ".wrap";

    constructor(protected geolocationDataProvider: GeolocationDataProvider) {
        TypeaheadMessage.subscribe(ElementFocusedMessage.getTopic(),
            (topic: string, message: ElementFocusedMessage) => {
                this.domElementId = message.getDomElementId();
            });
        TypeaheadMessage.subscribe(SuggestionSelectedMessage.getTopic(),
            (topic: string, message: SuggestionSelectedMessage) => {
                if (GeolocationDataProvider.CATEGORY === message.getSuggestion().getCategory().orElse("") &&
                    !this.isGeolocationAvailable()) {
                    this.geolocationDataProvider.findGeolocation(this.getDomElementId());
                }
            });
        TypeaheadMessage.subscribe(GeolocationRequestMessage.getTopic(),
            (topic: string, message: GeolocationRequestMessage) => {
                this.showInterstitial(this.getDomElementId());
            });
        TypeaheadMessage.subscribe(GeolocationResponseMessage.getTopic(),
            (topic: string, message: GeolocationResponseMessage) => {
                this.handleGeolocationResponse(message);
            });
    }

    public abstract handlePageLoad(tab: string): void;

    protected handleGeolocationResponse(message: GeolocationResponseMessage) {
        this.hideInterstitial(this.getDomElementId());
        if (message.getPositionError()) {
            this.showErrorModal(message.getPositionError().code, this.getDomElementId());
            $("#" + this.getDomElementId()).val("");
        } else {
            this.updateSearchParameters(message.getGeolocationResult(), $("#" + this.getDomElementId()));
            if (message.getSubmitButton()) {
                message.getSubmitButton().trigger("click");
            }
        }
    }

    protected getErrorMessage(code: number) {
        return this.errorConfigMap[code] ? this.errorConfigMap[code].message : this.errorConfigMap[4].message;
    }

    protected showInterstitial(domElementId: string): void {
        $("#" + domElementId).blur();
        uitk.publish("results.changed", {
            contentId: this.interstitialContentSelector,
            disable: true,
            message: "<p>" + GeolocationViewController.MESSAGES.FINDING_GEOLOCATION + "</p>",
            scrollToTop: false,
            showLoader: true,
        });
    }

    protected hideInterstitial(domElementId: string): void {
        uitk.publish("results.complete", {
            ariaLevel: "polite",
            contentId: this.interstitialContentSelector,
        });
    }

    protected showErrorModal(code: number, domElementId: string): void {
        let errorModal = uitk.modal.create({
            content: "<p>" + this.getErrorMessage(code) + "</p>",
            focusClose: domElementId,
            modalId: "geolocationErrorModal",
            title: GeolocationViewController.MESSAGES.ERROR_TITLE,
        });
        uitk.subscribe("modal.close", (topic, modal) => {
            if (modal ===  errorModal) {
                if (!this.errorConfigMap[code].closeTypeahead) {
                    $("#" + domElementId).trigger("focus", true);
                }
            }
        });
    }
    protected getDomElementId(): string {
        return this.domElementId ? this.domElementId :
            $("div.autocomplete:visible").find("input[name=destination]").attr("id");
    }

    protected abstract isGeolocationAvailable(): boolean;

    protected abstract updateSearchParameters(suggestionResult: ISuggestionResult, $element: JQuery): void;
}
